import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { getAvatar } from "../../utils/avatar";

export interface PremiumSelectOption {
  value: string;
  label: string;
  avatar?: string | null;
}

interface Props {
  options: PremiumSelectOption[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label?: string;
  showAvatar?: boolean;
  emptyLabel?: string;
  minWidth?: number;
  disabled?: boolean;
  allowClear?: boolean;
  /**
   * Async search hook. When supplied the parent controls the option set
   * (e.g. debounced student search) and the built-in in-memory `.filter`
   * is bypassed. Backwards-compatible: existing consumers omit it and
   * keep the local-filter behaviour they've always had.
   */
  onSearch?: (query: string) => void;
}

/**
 * A searchable dropdown with optional avatars per option. Used across
 * the Assessments and Attendance Reports pages to replace the browser's
 * default <select> elements.
 */
const PremiumSelect: React.FC<Props> = ({
  options, value, onChange,
  placeholder, label, showAvatar = false,
  emptyLabel, minWidth = 160, disabled, allowClear = true,
  onSearch,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
        if (onSearch) onSearch("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onSearch]);

  const selected = options.find((o) => o.value === value) || null;
  // With `onSearch` the parent owns the option set (already filtered by
  // an upstream query) so the local filter would double-narrow it.
  const filtered = onSearch
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  const avatarFor = (opt: PremiumSelectOption, size = 32) =>
    opt.avatar ? opt.avatar : getAvatar(opt.label, null, size);

  const triggerBase: CSSProperties = {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 14px", background: "#fff",
    border: open ? "2px solid #1b3a6b" : "1.5px solid #e2e8f0",
    borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    boxShadow: open ? "0 0 0 3px rgba(27,58,107,0.1)" : "none",
    minWidth, opacity: disabled ? 0.6 : 1, userSelect: "none",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <label style={{
          fontSize: 11, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.6px",
          display: "block", marginBottom: 6,
        }}>{label}</label>
      )}

      <div onClick={() => !disabled && setOpen((v) => !v)} style={triggerBase}>
        {showAvatar && selected && (
          <img
            src={avatarFor(selected, 26)}
            alt=""
            style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        )}
        <span style={{
          flex: 1, fontSize: 13, fontWeight: 500,
          color: selected ? "#1a1a2e" : "#94a3b8",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {selected?.label || placeholder || "Select…"}
        </span>
        {allowClear && selected && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            title="Clear"
            style={{ fontSize: 12, color: "#94a3b8", cursor: "pointer", padding: "0 4px" }}
          >×</span>
        )}
        <span style={{
          fontSize: 10, color: "#94a3b8",
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }}>▼</span>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)",
          left: 0, right: 0, zIndex: 500, minWidth,
          background: "#fff", borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          border: "1.5px solid #e2e8f0", overflow: "hidden",
          maxHeight: 320,
        }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 10, top: "50%",
                transform: "translateY(-50%)", fontSize: 13, color: "#94a3b8",
              }}>🔍</span>
              <input
                autoFocus
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (onSearch) onSearch(e.target.value); }}
                placeholder="Search…"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%", padding: "7px 10px 7px 32px",
                  border: "1.5px solid #e2e8f0", borderRadius: 8,
                  fontSize: 12, outline: "none", boxSizing: "border-box",
                  color: "#1a1a2e",
                }}
              />
            </div>
          </div>

          <div style={{ overflowY: "auto", maxHeight: 240 }}>
            {emptyLabel && (
              <div
                onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = !value ? "#f0f4ff" : "#fff")}
                style={{
                  padding: "10px 14px", cursor: "pointer",
                  background: !value ? "#f0f4ff" : "#fff",
                  fontSize: 13, color: "#94a3b8", fontWeight: 500,
                  borderBottom: "1px solid #f8fafc",
                }}
              >{emptyLabel}</div>
            )}

            {filtered.map((opt) => {
              const sel = opt.value === value;
              return (
                <div
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
                  onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = sel ? "#eff6ff" : "#fff")}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", cursor: "pointer",
                    background: sel ? "#eff6ff" : "#fff",
                    borderBottom: "1px solid #f8fafc",
                    transition: "background 0.1s",
                  }}
                >
                  {showAvatar && (
                    <img
                      src={avatarFor(opt, 32)}
                      alt=""
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        objectFit: "cover", border: "1.5px solid #e2e8f0",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span style={{
                    flex: 1, fontSize: 13, fontWeight: 500,
                    color: sel ? "#2563eb" : "#1a1a2e",
                  }}>{opt.label}</span>
                  {sel && <span style={{ color: "#2563eb", fontSize: 16 }}>✓</span>}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumSelect;
